import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalsService } from '@core/modals/modals.service';

import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  Seat,
  SummaryLinkModel,
  TransactionSeatDataItem,
  TransactionWrapper,
  Upgrade,
} from '@core/models';
import { AdobeDataPages } from '@core/models/adobe-data-layer.model';
import {
  CartService,
  ConfigurationService,
  UtilitiesService,
} from '@core/services';
import { AdobeDataLayerService } from '@core/services/adobe-data-layer-service.service';
import { TransactionService } from '@core/services/transaction.service';
import { Subscription } from 'rxjs';
import { UpgradesService } from '../../pages/select-seat/viewer/topbar/upgrades/upgrades.service';
import { DataBoxComponent } from './components/data-box/data-box.component';
import { ConnectionService } from './services/connection.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DataBoxComponent, NgOptimizedImage],
  selector: 'app-summary',
  standalone: true,
  templateUrl: './summary.component.html',
})
export class SummaryComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private adobeDataLayerService = inject(AdobeDataLayerService);
  private cartService = inject(CartService);
  private connectionService = inject(ConnectionService);
  private modalsService = inject(ModalsService);
  private router = inject(Router);
  private upgradesService = inject(UpgradesService);
  private utilitiesService = inject(UtilitiesService);
  private transactionService = inject(TransactionService);
  public isTicketFeeEnabled = signal(false);

  configurationService = inject(ConfigurationService);
  transactionId: string;
  validTransaction = signal(false);
  transaction = signal<TransactionWrapper | null>(null);
  summaryLink = signal<SummaryLinkModel | null>(null);
  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    const transaction = this.activatedRoute.snapshot.data[
      'transaction'
    ] as TransactionWrapper;
    this.transactionService.transaction$.next(transaction);
    this.transactionId = this.activatedRoute.snapshot.params['id'];
    this.connectionService.token =
      this.activatedRoute.snapshot.queryParams['token'];

    this.getIsClientTicketFeeEnabled();

    this.initializeComponent(transaction);

    this.adobeDataLayerService.pageEventSubject.subscribe(async page => {
      if (page !== AdobeDataPages.Summary) {
        return;
      }

      const ticket_addons = [
        {
          name: this.transactionService.getTransactionUpsell()?.name || '',
          price:
            this.transactionService.getTransactionUpsell()?.price?.toString() ||
            '0',
        },
      ];

      if (this.transactionService.hasRainoutInsurance()) {
        ticket_addons.push({
          name: 'rainout insurance',
          price: this.transactionService.getInsurancePrice()?.toString(),
        });
      }
      const event = this.adobeDataLayerService.getEvent();
      const gamePk = await this.adobeDataLayerService.getGamePK();
      const adobePayload = {
        event: 'purchase',
        ticketing: {
          events: [
            {
              event_id: this.adobeDataLayerService.getEventId(),
              matchup_desc: event?.description,
              matchup_dateTime: event?.date,
              matchup_full: this.adobeDataLayerService.getMatchupFull(),
              game_pk: gamePk,
              ticket_quantity: this.transactionService
                .getTotalSeats()
                ?.toString(),
              ticket_total_price: this.transactionService
                .getTotalPrice()
                ?.toString(),
              ticket_unit_price: this.transactionService
                .getUnitPrice()
                ?.toString(),
              ticket_seat_section:
                this.cartService.getSeatsSections()?.[0] || '',
              ticket_addons,
            },
          ],
        },
      };
      this.adobeDataLayerService.push(adobePayload);
    });

    (window as any)?.fbq?.('track', 'Purchase', {
      content_name: transaction?.transaction?.event?.description,
      value: transaction?.transaction?.tdcTransaction?.totalSalesRevenue,
      currency: 'USD',
      order_id:
        transaction?.transaction?.tdcTransaction?.cartId ||
        transaction?.transaction?.tdcTransaction?.transactionId,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  initializeComponent(transaction: TransactionWrapper) {
    if (transaction) {
      this.transaction.set({ ...transaction });
      let status = this.transaction()?.transaction.status;
      if (status === 'CA') {
        this.openCancelledTransactionModal();
      } else {
        const seats = [];
        this.validTransaction.set(true);

        if (this.transaction()?.transaction.seatsPrices) {
          const seatsPricesEntries = Object.entries(
            this.transaction()?.transaction?.seatsPrices as Record<
              string,
              TransactionSeatDataItem
            >
          );

          for (const [mmcId, price] of seatsPricesEntries) {
            let seat = this.utilitiesService.splittedSeatInfoFromId(
              mmcId
            ) as Seat;
            seat.price = price.value;
            seats.push(seat);
          }
        }

        this.upgradesService.upgrades = this.transaction()?.transaction?.event
          ?.upgrades as Upgrade[];
        this.upgradesService.checkCartSeatsForUpgradesList(seats.length);
        this.getSummaryLink(this.transaction()?.transaction?.event?.pvEventId);
      }
    } else {
      const modalData = {
        title: 'ERROR',
        content: 'An Error occurred while trying to get Transaction data.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
    }
  }

  getSummaryLink(eventId: number | undefined) {
    if (!eventId) return;

    const subscription = this.connectionService
      .getSummaryLink(eventId)
      .subscribe({
        next: response => {
          this.summaryLink.set(response.data as SummaryLinkModel);
        },
        error: console.error,
      });

    this.subscriptions.push(subscription);
  }

  openCancelledTransactionModal() {
    const modalData = {
      title: 'Transaction Cancelled',
      content: 'The transaction has been cancelled.',
      acceptBtnName: 'CLOSE',
      acceptFunction: () => {
        this.router.navigate(['/events']);
      },
    };
    this.modalsService.openModal(modalData);
  }

  private getIsClientTicketFeeEnabled() {
    const getCurrentClient$ = this.connectionService
      .getCurrentClient()
      .subscribe(response => {
        this.isTicketFeeEnabled.set(!!response.data?.isTicketFeeEnabled);
      });

    this.subscriptions.push(getCurrentClient$);
  }
}
